import './index.css';
import type { ThemeConfig } from '../../themes/types';
import { IBimComponent } from '../../types/component';
import { themeManager } from '../../services/theme';
import type {
    EngineOptions,
    ModelLoadOptions,
    EngineInfo,
    EngineSettings,
    EngineSettingsPatch,
    EngineSettingPreset,
    SettingPresetLists,
    ViewItem,
    ViewTreeData,
    ModelViewTree,
} from './types';
import { type MeasureMode, type ClearHeightDirection, type ClearHeightSelectType } from '../../types/measure';
import type { MeasureUnit, MeasurePrecision } from '../measure-panel/types';
import type { SectionBoxRange } from '../section-box-panel/types';
import type { ManagerRegistry } from '../../core/manager-registry';
import { getIcon } from '../../utils/icon-manager';
import { HelpPanel } from '../help-panel';
import { localeManager } from '../../services/locale';
import type { DrawingPinRecord } from '../../types/events';
// 导入第三方 SDK 的 createEngine 函数（从 npm 包引入）
import { createEngine as createEngineSDK } from 'iflow-engine-base';

export type {
    EngineOptions,
    ModelLoadOptions,
    EngineInfo,
    EngineSettings,
    EngineSettingsPatch,
    EngineSettingPreset,
    SettingPresetLists,
    ViewItem,
    ViewTreeData,
    ModelViewTree,
};

/**
 * 创建 Engine 实例的工厂函数
 * 兼容旧代码直接 import { createEngine } 的方式
 */
export const createEngine = (options: EngineOptions, registry: ManagerRegistry) => {
    return new Engine(options, registry);
};

/**
 * 3D 引擎组件
 * 负责创建和管理第三方 3D 引擎实例
 */
export class Engine implements IBimComponent {
    /** 第三方 3D 引擎实例 */
    /** 第三方 3D 引擎实例 */
    private engine: any = null;
    /** 管理器注册表实例 */
    private registry: ManagerRegistry;
    /** 引擎挂载的容器元素 */
    private container: HTMLElement;
    /** 引擎容器 ID（用于传递给 createEngine） */
    private containerId: string;
    /** 引擎配置选项（不包含 container） */
    private options: Omit<EngineOptions, 'container'>;
    /** 是否已初始化 */
    private _isInitialized = false;
    /** 是否已销毁 */
    private _isDestroyed = false;
    /** 主题订阅取消函数 */
    private unsubscribeTheme: (() => void) | null = null;
    /** 当前激活的测量类型 */
    private currentMeasureType: MeasureMode | null = null;
    /** 主测量功能是否已激活 */
    private isMeasureActive: boolean = false;
    /** 当前激活的剖切模式 */
    private currentSectionMode: 'x' | 'y' | 'z' | 'box' | 'face' | null = null;
    /** 当前选中的构件信息 */
    private selectedComponent: { url: string; id: string } | null = null;
    private viewDropdownTrigger: HTMLElement | null = null;
    private viewDropdownMenu: HTMLElement | null = null;
    private infoButton: HTMLElement | null = null;
    private helpPanel: HelpPanel | null = null;
    private unsubscribeLocale: (() => void) | null = null;

    /**
     * 构造函数
     * @param options 3D 引擎配置选项
     */
    constructor(options: EngineOptions, registry: ManagerRegistry) {
        // 保存注册表
        this.registry = registry;
        // 解析容器元素
        this.container = options.container;
        // 如果容器没有 id，生成一个唯一的 id
        if (!this.container.id) {
            this.containerId = `engine-container-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
            this.container.id = this.containerId;
        } else {
            this.containerId = this.container.id;
        }

        // 保存配置选项（设置默认值）
        this.options = {
            backgroundColor: 'linear-gradient(to bottom, rgb(214, 224, 235), rgb(246, 250, 255))', // 固定背景渐变色
            version: options.version ?? 'v2', // 默认使用 v2 版本
            showStats: options.showStats ?? false, // 默认不显示统计
            showViewCube: options.showViewCube ?? true, // 默认显示视图立方体
        };
    }

    /**
     * 初始化组件 (接口实现)
     * 创建 div 容器并初始化引擎
     */
    public init(): void {
        if (this._isInitialized) {
            console.warn('[Engine] Engine already initialized.');
            return;
        }

        if (this._isDestroyed) {
            console.error('[Engine] Cannot initialize destroyed engine.');
            return;
        }

        try {
            // 应用背景色到容器
            if (typeof this.options.backgroundColor === 'string') {
                this.container.style.background = this.options.backgroundColor;
            }

            // 创建引擎配置对象
            const engineConfig = {
                containerId: this.containerId,
                backgroundColor: this.options.backgroundColor,
                version: this.options.version,
                showStats: this.options.showStats,
                showViewCube: this.options.showViewCube,
            };

            // 输出配置信息
            console.log('引擎配置信息：', engineConfig);

            // 调用引擎创建函数创建引擎实例
            // 将 options 中的配置复制给 createEngine
            this.engine = createEngineSDK(engineConfig);

            if (!this.engine) {
                throw new Error('Failed to create engine instance');
            }

            // 标记为已初始化
            this._isInitialized = true;

            this.resize(this.container.clientWidth, this.container.clientHeight);

            // 订阅主题变化
            this.unsubscribeTheme = themeManager.subscribe((theme) => {
                this.setTheme(theme);
            });

            // 应用当前主题
            this.setTheme(themeManager.getTheme());

            // 监听构件点击事件
            // 底层 interactionModule trigger 格式: [{url: string, ids: string[]}]
            if (this.engine?.events?.on) {
                this.engine.events.on('click', (data: any) => {
                    const registry = this.registry;
                    console.log('[Engine] 底层 click 事件触发:', data);
                    if (data && Array.isArray(data) && data.length > 0 && data[0].url) {
                        this.selectedComponent = {
                            url: data[0].url,
                            id: data[0].ids?.[0]
                        };
                        console.log('[Engine] 触发 component:selected:', this.selectedComponent);
                        registry.emit('component:selected', this.selectedComponent);
                    } else {
                        this.selectedComponent = null;
                        console.log('[Engine] 触发 component:deselected');
                        registry.emit('component:deselected', {});
                    }
                });

                // 监听模型加载完成事件
                this.engine.events.on('loading_completed', () => {
                    console.log('[Engine] 底层 LoadingCompleted 事件触发');
                    this.registry.emit('engine:model-loading-completed', {});
                });
            } else {
                console.warn('[Engine] 底层引擎不支持 events.on 方法，无法监听点击事件');
            }

            const oneClickEncoding = (this.engine as any).oneClickEncoding;
            if (oneClickEncoding?.on) {
                oneClickEncoding.on('encoding-start', (data: any) => {
                    console.log('[Engine] 底层 encoding-start 事件触发:', data);
                    this.registry.emit('encoding:start', data);
                });
                oneClickEncoding.on('encoding-complete', (data: any) => {
                    console.log('[Engine] 底层 encoding-complete 事件触发:', data);
                    this.registry.emit('encoding:complete', data);
                });
                oneClickEncoding.on('encoding-error', (data: any) => {
                    console.log('[Engine] 底层 encoding-error 事件触发:', data);
                    this.registry.emit('encoding:error', data);
                });
            }

            this.createViewDropdown();
            this.initHelpPanel();
            this.createInfoButton();

            this.unsubscribeLocale = localeManager.subscribe(() => {
                this.updateViewDropdownText();
            });

        } catch (error) {
            console.error('[Engine] Failed to initialize engine:', error);
            this._isInitialized = false;
            throw error;
        }
    }

    /**
     * 设置主题 (接口实现)
     * 根据主题调整 3D 引擎的视觉效果（如背景色）
     * @param theme 全局主题配置
     */
    public setTheme(_theme: ThemeConfig): void {

    }

    /**
     * 设置语言 (接口实现)
     */
    public setLocales(): void {
        // 3D 引擎组件暂时不需要本地化
    }

    /**
     * 检查是否已初始化
     */
    public isInitialized(): boolean {
        return this._isInitialized;
    }


    /**
     * 加载 3D 模型
     * @param urls 模型文件 URL 数组
     * @param options 加载选项（位置、旋转、缩放）
     */
    public loadModel(urls: string[], options?: ModelLoadOptions): void {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Engine not initialized. Please call init() first.');
            return;
        }
        if (!urls || urls.length === 0) {
            console.error('[Engine] Model URLs are required.');
            return;
        }
        this.engine.loaderModule.loadModels(urls, options);
    }

    /**
     * 回到主视角
     * @param duration 动画持续时间（毫秒），默认由底层决定
     * @returns
     */
    public CameraGoHome(duration?: number) {
        this.engine.viewCube.CameraGoHome(duration);
    }

    public switchToPerspectiveCamera(): void {
        if (!this._isInitialized || !this.engine?.cameraModule) {
            console.warn('[Engine] Cannot switch to perspective camera: engine not initialized.');
            return;
        }
        this.engine.cameraModule.switchToPerspectiveCamera();
    }

    public saveMainView(): any {
        if (!this._isInitialized || !this.engine?.viewCube) {
            console.warn('[Engine] Cannot save main view: engine not initialized.');
            return null;
        }
        const viewData = this.engine.viewCube.saveMainViewPort();
        this.registry.emit('view:main-view-saved', { viewData });
        return viewData;
    }

    public restoreMainView(): void {
        if (!this._isInitialized || !this.engine?.viewCube) {
            console.warn('[Engine] Cannot reset main view: engine not initialized.');
            return;
        }
        this.engine.viewCube.resetMainViewPort();
        this.registry.emit('view:main-view-restored', {});
    }

    public setMainViewPort(viewData: any): void {
        if (!this._isInitialized || !this.engine?.viewCube) {
            console.warn('[Engine] Cannot set main view: engine not initialized.');
            return;
        }
        this.engine.viewCube.setMainViewPort(viewData);
    }

    public captureScreenshot(): void {
        if (!this._isInitialized || !this.engine?.viewCube) {
            console.warn('[Engine] Cannot capture screenshot: engine not initialized.');
            return;
        }
        this.engine.viewCube.screenshot();
    }

    private createViewDropdown(): void {
        const trigger = document.createElement('div');
        trigger.className = 'engine-view-dropdown-trigger';
        trigger.innerHTML = getIcon('doubleArrowDown');
        trigger.title = localeManager.t('viewDropdown.triggerTitle');
        trigger.addEventListener('click', (e) => {
            e.stopPropagation();
            this.toggleViewDropdown();
        });
        this.container.appendChild(trigger);
        this.viewDropdownTrigger = trigger;
    }

    private initHelpPanel(): void {
        this.helpPanel = new HelpPanel({
            onDontRemind: () => {
                this.registry.emit('help:dont-remind', {});
            },
        });
        this.helpPanel.init();
        this.container.appendChild(this.helpPanel.element);
    }

    public showHelpPanel(): void {
        this.helpPanel?.show();
    }

    private createInfoButton(): void {
        const button = document.createElement('div');
        button.className = 'engine-info-button';
        button.innerHTML = getIcon('default');
        button.title = '信息';
        button.addEventListener('click', (e) => {
            e.stopPropagation();
            this.helpPanel?.show();
        });
        this.container.appendChild(button);
        this.infoButton = button;
    }

    private toggleViewDropdown(): void {
        if (this.viewDropdownMenu) {
            this.closeViewDropdown();
            return;
        }

        const menu = document.createElement('div');
        menu.className = 'engine-view-dropdown-menu';

        const items = [
            { key: 'switchOrthographic', action: () => this.switchToOrthographicCamera() },
            { key: 'switchPerspective', action: () => this.switchToPerspectiveCamera() },
            { key: 'saveMainView', action: () => this.saveMainView() },
            { key: 'restoreMainView', action: () => this.restoreMainView() },
            { key: 'captureScreenshot', action: () => this.captureScreenshot() },
        ];

        items.forEach(item => {
            const row = document.createElement('div');
            row.className = 'engine-view-dropdown-item';
            row.dataset.key = item.key;
            row.textContent = localeManager.t(`viewDropdown.${item.key}`);
            row.addEventListener('click', (e) => {
                e.stopPropagation();
                item.action();
                this.closeViewDropdown();
            });
            menu.appendChild(row);
        });

        const rect = this.viewDropdownTrigger!.getBoundingClientRect();
        menu.style.position = 'fixed';
        menu.style.top = `${rect.bottom + 4}px`;
        menu.style.right = `${window.innerWidth - rect.right}px`;
        menu.style.zIndex = '10000';

        document.body.appendChild(menu);
        this.viewDropdownMenu = menu;

        const closeHandler = (e: MouseEvent) => {
            if (!menu.contains(e.target as Node)) {
                this.closeViewDropdown();
                document.removeEventListener('mousedown', closeHandler);
            }
        };
        document.addEventListener('mousedown', closeHandler);
    }

    private closeViewDropdown(): void {
        if (this.viewDropdownMenu) {
            this.viewDropdownMenu.remove();
            this.viewDropdownMenu = null;
        }
    }

    private updateViewDropdownText(): void {
        if (!this.viewDropdownTrigger) return;
        this.viewDropdownTrigger.title = localeManager.t('viewDropdown.triggerTitle');
        if (!this.viewDropdownMenu) return;
        const items = this.viewDropdownMenu.querySelectorAll('.engine-view-dropdown-item');
        const keys = ['switchOrthographic', 'switchPerspective', 'saveMainView', 'restoreMainView', 'captureScreenshot'];
        items.forEach((item, index) => {
            if (keys[index]) {
                item.textContent = localeManager.t(`viewDropdown.${keys[index]}`);
            }
        });
    }

    /**
     * 订阅原始引擎事件
     * 用于需要访问底层引擎事件的场景（如测量回调、剖切移动等）
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public onRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.on(event, handler);
    }

    /**
     * 取消订阅原始引擎事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public offRawEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.events?.off(event, handler);
    }

    /**
     * 订阅一键编码事件
     * @param event 事件名称：'encoding-start' | 'encoding-complete' | 'encoding-error'
     * @param handler 事件处理函数
     */
    public onOneClickEncodingEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.oneClickEncoding?.on(event, handler);
    }

    /**
     * 取消订阅一键编码事件
     * @param event 事件名称
     * @param handler 事件处理函数
     */
    public offOneClickEncodingEvent(event: string, handler: (...args: any[]) => void): void {
        this.engine?.oneClickEncoding?.off(event, handler);
    }

    /**
     * 启动一键编码
     * @remarks 调用底层 engine.oneClickEncoding.start()，需先订阅相关事件
     */
    public startOneClickEncoding(): void {
        if (!this._isInitialized || !this.engine?.oneClickEncoding) {
            console.warn('[Engine] oneClickEncoding not available.');
            return;
        }
        this.engine.oneClickEncoding.start();
    }

    /**
     * 暂停渲染
     */
    public pauseRendering(): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Engine not initialized.');
            return;
        }
        this.engine.pauseRendering();
    }

    /**
     * 恢复渲染
     */
    public resumeRendering(): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Engine not initialized.');
            return;
        }
        this.engine.resumeRendering();
    }

    /**
     * 创建当前视点快照
     * @param callback 底层完成后的回调，参数为视点数据对象
     */
    public createDrawingPin(callback: (pin: any) => void): void {
        console.log('[Engine] createDrawingPin called');
        if (!this._isInitialized || !this.engine?.drawingPin) {
            console.warn('[Engine] drawingPin.create not available. initialized:', this._isInitialized, 'drawingPin:', !!this.engine?.drawingPin);
            return;
        }
        console.log('[Engine] calling this.engine.drawingPin.create');
        this.engine.drawingPin.create((pin: any) => {
            console.log('[Engine] drawingPin.create callback received:', pin);
            callback(pin);
        });
    }

    /**
     * 还原视点快照
     * @param pin 视点数据对象
     * @param callback 底层 restore 完成后的回调
     */
    public async restoreDrawingPin(pin: any, callback?: (result: any) => void): Promise<void> {
        console.log('[Engine] restoreDrawingPin called:', pin);
        if (!this._isInitialized || !this.engine?.drawingPin) {
            console.warn('[Engine] drawingPin.restore not available.');
            return;
        }
        return this.engine.drawingPin.restore(pin, 'edit', (result: any) => {
            console.log('[Engine] drawingPin.restore callback received:', result);
            callback?.(result);
        });
    }

    /**
     * 直接还原视点快照
     * @param snapshot 视点快照对象
     * @param callback 底层 restore 完成后的回调
     */
    public async restoreViewSnapshot(snapshot: any): Promise<void> {
        console.log('[Engine] restoreViewSnapshot called:', snapshot);
        if (!this._isInitialized || !this.engine?.drawingPin) {
            console.warn('[Engine] drawingPin.restore not available.');
            return;
        }
        return this.engine.drawingPin.restore(snapshot);
    }

    /**
     * 设置图钉记录列表（平铺结构，由组件内部构建树）
     * @param records 图钉记录列表
     */
    public setPinRecords(records: DrawingPinRecord[]): void {
        console.log('[Engine] setPinRecords called, records count:', records.length);
        this.registry.emit('drawingPin:list-updated', { records });
    }

    /**
     * 兼容旧命名，内部仍按记录列表处理。
     */
    public setPinList(records: DrawingPinRecord[]): void {
        this.setPinRecords(records);
    }

    /**
     * 销毁 3D 引擎，释放 GPU 资源
     */
    public dispose(): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Engine not initialized.');
            return;
        }
        this.closeViewDropdown();
        if (this.viewDropdownTrigger) {
            this.viewDropdownTrigger.remove();
            this.viewDropdownTrigger = null;
        }
        if (this.infoButton) {
            this.infoButton.remove();
            this.infoButton = null;
        }
        if (this.helpPanel) {
            this.helpPanel.destroy();
            this.helpPanel = null;
        }
        if (this.unsubscribeLocale) {
            this.unsubscribeLocale();
            this.unsubscribeLocale = null;
        }
        this.engine.dispose();
    }

    /**
     * 调整渲染器尺寸
     * 容器大小变化时调用，自动更新渲染器、相机投影矩阵和后处理合成器
     */
    public resize(width?: number, height?: number): void {
        if (!this._isInitialized || !this.engine) {
            return;
        }

        const nextWidth = typeof width === 'number' ? Math.floor(width) : this.container.clientWidth;
        const nextHeight = typeof height === 'number' ? Math.floor(height) : this.container.clientHeight;

        if (nextWidth <= 0 || nextHeight <= 0) {
            return;
        }

        if (typeof this.engine.handleWindowResize === 'function') {
            this.engine.handleWindowResize(nextWidth, nextHeight);
        }
    }

    // ==================== 测量功能方法 ====================

    // ==================== 测量功能（统一 API） ====================

    /**
     * 激活测量功能
     * @param mode 测量类型
     *   - 'clearHeight': 净高测量
     *   - 'clearDistance': 净距测量
     *   - 'distance': 距离测量 y
     *   - 'elevation': 标高测量 y
     *   - 'point': 坐标测量
     *   - 'angle': 角度测量 y
     *   - 'area': 面积测量 
     *   - 'slope': 坡度测量 y
     */
    public activateMeasure(mode: MeasureMode): void {
        if (!this._isInitialized || !this.engine?.measure) {
            console.error('[Engine] Cannot activate measure: engine not initialized.');
            return;
        }

        if (this.currentMeasureType === mode) {
            console.log(`[Engine] Measure ${mode} already active, skipping.`);
            return;
        }

        // 先激活测量模块
        if (!this.isMeasureActive) {
            console.log('[Engine] Activating measure module');
            this.engine.measure.active();
            this.isMeasureActive = true;
        }

        console.log(`[Engine] Activating measure: ${mode}`);
        const measure = this.engine.measure;

        switch (mode) {
            case 'clearHeight':
                measure.clearHeightMeasure.active();
                break;
            case 'clearDistance':
                // measure.clearDistanceMeasure.active();
                measure.faceDistanceMeasure.active();
                break;
            case 'distance':
                measure.distanceMeasure.active();
                break;
            case 'elevation':
                measure.elevationMeasure.active();
                break;
            case 'point':
                measure.pointMeasure.active();
                break;
            case 'angle':
                measure.angleMeasure.active();
                break;
            case 'area':
                measure.areaMeasure.active();
                break;
            case 'slope':
                measure.slopeMeasure.active();
                break;
            default:
                console.error(`[Engine] Unknown measure type: ${mode}`);
                return;
        }
        this.currentMeasureType = mode;
    }

    /**
     * 获取当前激活的测量类型
     * @returns 当前测量类型，未激活时返回 null
     */
    public getCurrentMeasureType(): MeasureMode | null {
        return this.currentMeasureType;
    }

    /**
     * 停用测量功能
     * @remarks 关闭测量时调用，会同时清除所有测量标注
     */
    public deactivateMeasure(): void {
        if (!this._isInitialized || !this.engine?.measure) {
            return;
        }

        console.log('[Engine] Deactivating measure');
        this.engine.measure.disActive();
        this.engine.measure.clearAll();
        this.isMeasureActive = false;
        this.currentMeasureType = null;
    }

    public resetMeasureState(): void {
        this.currentMeasureType = null;
        this.isMeasureActive = false;
    }

    /**
     * 清除所有测量标注（不停用测量模式）
     */
    public clearAllMeasures(): void {
        if (!this._isInitialized || !this.engine?.measure) {
            return;
        }
        console.log('[Engine] Clearing all measures');
        this.engine.measure.clearAll();
    }

    /**
     * 保存测量设置
     * @param setting 测量设置，包含单位和精度
     */
    public saveMeasureSetting(setting: { unit: MeasureUnit; precision: MeasurePrecision }): void {
        if (!this._isInitialized || !this.engine?.measure) {
            return;
        }
        this.engine.measure.saveSetting?.(setting);
    }

    /**
     * 设置净高测量朝向
     * @param direction 0=朝下，1=朝上
     */
    public setClearHeightDirection(direction: ClearHeightDirection): void {
        if (!this._isInitialized || !this.engine?.measure) {
            return;
        }
        console.log(`[Engine] Setting clearHeight direction: ${direction}`);
        this.engine.measure.clearHeightMeasure.setDirection(direction);
    }

    /**
     * 设置净高测量选择对象类型
     * @param selectType 'point'=选择点，'element'=选择构件
     */
    public setClearHeightSelectType(selectType: ClearHeightSelectType): void {
        if (!this._isInitialized || !this.engine?.measure) {
            return;
        }
        console.log(`[Engine] Setting clearHeight selectType: ${selectType}`);
        this.engine.measure.clearHeightMeasure.setSelectType(selectType);
    }

    // ==================== 结束：测量功能 ====================

    // ==================== 剖切功能（统一 API） ====================

    /**
     * 激活剖切功能（统一入口）
     * @param mode 剖切模式
     *   - 'x': X轴剖切面
     *   - 'y': Y轴剖切面
     *   - 'z': Z轴剖切面
     *   - 'box': 剖切盒（六面体裁剪）
     *   - 'face': 拾取面剖切（点选模型表面创建剖切面）
     * @remarks
     * - 幂等操作：如果当前模式与请求模式相同，则静默返回
     * - 切换模式时会自动停用之前的剖切
     * - 底层调用 engine.clipping.active(mode)
     * @example
     * ```typescript
     * // 激活 X 轴剖切
     * engine.activeSection('x');
     * 
     * // 切换到剖切盒
     * engine.activeSection('box');
     * ```
     */
    public activeSection(mode: 'x' | 'y' | 'z' | 'box' | 'face'): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot activate section: engine not initialized.');
            return;
        }
        if (this.currentSectionMode === mode) {
            console.log(`[Engine] Section ${mode} already active, skipping.`);
            return;
        }
        console.log(`[Engine] Activating section: ${mode}`);
        this.engine.clipping.active(mode);
        this.currentSectionMode = mode;
    }

    /**
     * 获取当前激活的剖切模式
     * @returns 当前剖切模式，未激活时返回 null
     * @example
     * ```typescript
     * const mode = engine.getCurrentSectionMode();
     * if (mode === 'box') {
     *     console.log('剖切盒已激活');
     * }
     * ```
     */
    public getCurrentSectionMode(): 'x' | 'y' | 'z' | 'box' | 'face' | null {
        return this.currentSectionMode;
    }

    /**
     * 设置剖切盒范围
     * @param range 剖切范围配置，每个轴的 min/max 值为百分比 (0-100)
     * @remarks
     * - 仅在 'box' 模式下有效
     * - 底层调用 engine.clipping.updateClippingValue(range)
     * @example
     * ```typescript
     * // 设置 X 轴裁剪 20%-80%，Y/Z 轴保持完整
     * engine.setSectionBoxRange({
     *     x: { min: 20, max: 80 },
     *     y: { min: 0, max: 100 },
     *     z: { min: 0, max: 100 }
     * });
     * ```
     */
    public setSectionBoxRange(range: SectionBoxRange): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot set section box range: engine not initialized.');
            return;
        }
        this.engine.clipping.updateClippingValue(range);
    }

    /**
     * 停用所有剖切功能
     * @remarks
     * - 统一停用入口，无论当前是哪种剖切模式
     * - 关闭剖切对话框时调用此方法
     * - 底层调用 engine.clipping.disActive()
     * @example
     * ```typescript
     * // 关闭剖切
     * engine.deactivateSection();
     * ```
     */
    public deactivateSection(): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            return;
        }
        console.log('[Engine] Deactivating all sections');
        this.engine.clipping.disActive();
        this.currentSectionMode = null;
    }

    /**
     * 隐藏剖切面（临时隐藏，可恢复）
     * @remarks
     * - 隐藏当前剖切面，但不停用剖切功能
     * - 配合 recoverSection() 使用
     * - 底层调用 engine.clipping.disabled()
     * @example
     * ```typescript
     * // 临时隐藏剖切面
     * engine.hideSection();
     * // 稍后恢复
     * engine.recoverSection();
     * ```
     */
    public hideSection(): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot hide section: engine not initialized.');
            return;
        }
        console.log('[Engine] Hiding section');
        this.engine.clipping.disabled();
    }

    /**
     * 恢复剖切面（从隐藏状态恢复）
     * @remarks
     * - 恢复被 hideSection() 隐藏的剖切面
     * - 底层调用 engine.clipping.recover()
     * @example
     * ```typescript
     * // 隐藏后恢复剖切面
     * engine.hideSection();
     * // ... 一段时间后
     * engine.recoverSection();
     * ```
     */
    public recoverSection(): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot recover section: engine not initialized.');
            return;
        }
        console.log('[Engine] Recovering section');
        this.engine.clipping.recover();
    }

    /**
     * 剖切盒适应选中模型
     * @remarks 将剖切盒调整为刚好包围选中的模型
     */
    public fitSectionBoxToModel(): void {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot fit section box: engine not initialized.');
            return;
        }
        const model = this.engine.engineStatus?.highlightModels;
        if (model) {
            console.log('[Engine] Fitting section box to model');
            this.engine.clipping?.clippingModel(model);
        }
    }

    /**
     * 剖切盒适应（缩放到场景整体包围盒）
     * @remarks
     * - 对接底层 `engine.clipping.scaleBox()`
     * - 会确保当前处于剖切盒模式
     */
    public scaleSectionBox(): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot scale section box: engine not initialized.');
            return;
        }
        this.engine.clipping.scaleBox();
    }

    /**
     * 反向剖切
     * @remarks 对接底层 `engine.clipping.reverse()`
     */
    public reverseSection(): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot reverse section: engine not initialized.');
            return;
        }
        this.engine.clipping.reverse();
    }

    /**
     * 设置剖切面填充开关
     * @param enabled 是否启用填充
     * @remarks 对接底层 `engine.clipping.setFillCutFace(enabled)`
     */
    public setFillCutFace(enabled: boolean): void {
        if (!this._isInitialized || !this.engine?.clipping) {
            console.error('[Engine] Cannot set fill cut face: engine not initialized.');
            return;
        }
        console.log('[Engine] Setting fill cut face:', enabled);
        this.engine.clipping.setFillCutFace(enabled);
    }

    /**
     * 获取剖切面填充状态
     * @returns true=已启用填充，false=未启用
     * @remarks 对接底层 `engine.clipping.getFillCutFace()`
     */
    public getFillCutFace(): boolean {
        if (!this._isInitialized || !this.engine?.clipping) {
            return false;
        }
        return this.engine.clipping.getFillCutFace?.() ?? false;
    }

    // ==================== 结束：剖切功能 ====================

    // ==================== 相机切换 ====================

    /**
     * 切换相机类型（透视/正交）
     * @remarks 底层调用 cameraModule.switchCurrentCamera()，保留当前相机位置
     */
    public switchCamera(): void {
        if (!this._isInitialized || !this.engine?.cameraModule) {
            console.warn('[Engine] Cannot switch camera: engine not initialized.');
            return;
        }
        this.engine.cameraModule.switchCurrentCamera();
    }

    /**
     * 获取当前相机类型
     * @returns 'perspective' | 'orthographic'
     */
    public getCameraType(): 'perspective' | 'orthographic' {
        if (!this._isInitialized || !this.engine?.cameraModule) {
            return 'perspective';
        }
        // 底层 CameraType enum: PERSPECTIVE = 0, ORTHOGRAPHIC = 1
        const type = this.engine.cameraModule.getCameraType();
        return type === 1 ? 'orthographic' : 'perspective';
    }

    // ==================== 结束：相机切换 ====================
    // ==================== 渲染模式 ====================

    /**
     * 获取当前渲染模式
     * @returns 'simple' | 'balance' | 'advanced'
     */
    public getRenderMode(): string {
        if (!this._isInitialized || !this.engine?.engineModelModule) {
            return 'balance';
        }
        return this.engine.engineModelModule.getCurrentMode();
    }

    /**
     * 设置渲染模式
     * @param mode 'simple'(性能模式) | 'balance'(平衡模式) | 'advanced'(效果模式)
     */
    public setRenderMode(mode: 'simple' | 'balance' | 'advanced'): void {
        if (!this._isInitialized || !this.engine?.engineModelModule) {
            console.warn('[Engine] Cannot set render mode: engine not initialized.');
            return;
        }
        try {
            this.engine.engineModelModule.setMode(mode);
        } catch (e) {
            console.warn('[Engine] Failed to set render mode:', e);
        }
    }

    // ==================== 结束：渲染模式 ====================

    // ==================== 设置功能 ====================

    private normalizePercent(value: number, fallback: number): number {
        if (!Number.isFinite(value)) return fallback;
        return Math.min(100, Math.max(0, Math.round(value)));
    }

    private getSettingApi(): Record<string, unknown> | null {
        if (!this._isInitialized || !this.engine?.setting) {
            return null;
        }
        return this.engine.setting as Record<string, unknown>;
    }

    private callSettingMethod<T = unknown>(methodName: string, ...args: unknown[]): T | undefined {
        const settingApi = this.getSettingApi();
        if (!settingApi) return undefined;
        const method = settingApi[methodName];
        if (typeof method !== 'function') return undefined;
        return (method as (...innerArgs: unknown[]) => T).apply(this.engine.setting, args);
    }

    private hasSettingMethod(methodName: string): boolean {
        const settingApi = this.getSettingApi();
        if (!settingApi) return false;
        return typeof settingApi[methodName] === 'function';
    }

    private mergeSettingsWithDefaults(raw: Partial<EngineSettings>): EngineSettings {
        const groundId = raw.display?.groundId ?? '0';
        const showGround = typeof raw.display?.showGround === 'boolean'
            ? raw.display.showGround
            : groundId !== '0' && groundId !== '';

        return {
            render: {
                mode: raw.render?.mode ?? 'advanced',
                contrast: this.normalizePercent(raw.render?.contrast ?? 50, 50),
                saturation: this.normalizePercent(raw.render?.saturation ?? 50, 50),
                shadowIntensity: this.normalizePercent(raw.render?.shadowIntensity ?? 50, 50),
                lightIntensity: this.normalizePercent(raw.render?.lightIntensity ?? 50, 50),
                gtaoIntensity: this.normalizePercent(raw.render?.gtaoIntensity ?? 50, 50),
            },
            display: {
                showEdge: Boolean(raw.display?.showEdge),
                edgeOpacity: this.normalizePercent(raw.display?.edgeOpacity ?? 30, 30),
                showGrid: Boolean(raw.display?.showGrid),
                showLevel: Boolean(raw.display?.showLevel),
                showGround,
                groundId,
                groundHeight: Number.isFinite(raw.display?.groundHeight) ? Number(raw.display?.groundHeight) : 0,
            },
            environment: {
                type: raw.environment?.type ?? 'none',
                hdrId: raw.environment?.hdrId ?? '0',
                hdrIntensity: this.normalizePercent(raw.environment?.hdrIntensity ?? 20, 20),
                skyPreset: raw.environment?.skyPreset ?? 'sunrise_clear',
                skyParams: raw.environment?.skyParams ?? {},
                skyIntensity: this.normalizePercent(raw.environment?.skyIntensity ?? 20, 20),
            },
        };
    }

    public getPresetLists(): SettingPresetLists {
        const fallback: SettingPresetLists = { ground: [], hdr: [], sky: [] };
        const settingApi = this.getSettingApi();
        if (!settingApi) return fallback;

        const getter = settingApi.getPresetLists;
        const raw = typeof getter === 'function'
            ? (getter.call(this.engine.setting) as Partial<SettingPresetLists> | null)
            : null;

        const legacyGroundList = this.getGroundList().map((item) => ({
            id: item.id,
            names: [item.name, item.name, item.name],
        }));
        const legacyHdrList = this.getHDRBackgroundList().map((item) => ({
            id: item.id,
            names: [item.name, item.name, item.name],
        }));

        return {
            ground: Array.isArray(raw?.ground) && raw.ground.length > 0 ? raw.ground : legacyGroundList,
            hdr: Array.isArray(raw?.hdr) && raw.hdr.length > 0 ? raw.hdr : legacyHdrList,
            sky: Array.isArray(raw?.sky) ? raw.sky : [],
        };
    }

    public getSettings(): EngineSettings {
        const raw = this.callSettingMethod<Partial<EngineSettings>>('getSettings');
        if (raw && typeof raw === 'object') {
            return this.mergeSettingsWithDefaults(raw);
        }

        return {
            render: {
                mode: this.getRenderMode() as EngineSettings['render']['mode'],
                contrast: this.normalizePercent(this.getSceneContrast(), 50),
                saturation: this.normalizePercent(this.getSceneSaturation(), 50),
                shadowIntensity: 50,
                lightIntensity: this.normalizePercent(this.getAmbientLightIntensity(), 50),
                gtaoIntensity: 50,
            },
            display: {
                showEdge: this.getModelEdgeActive(),
                edgeOpacity: 30,
                showGrid: false,
                showLevel: false,
                showGround: this.getGroundId() !== '0' && this.getGroundId() !== '',
                groundId: this.getGroundId() || '0',
                groundHeight: this.getGroundElevation(),
            },
            environment: {
                type: this.getHDRBackgroundId() && this.getHDRBackgroundId() !== '0' ? 'hdr' : 'none',
                hdrId: this.getHDRBackgroundId() || '0',
                hdrIntensity: 20,
                skyPreset: 'sunrise_clear',
                skyParams: {},
                skyIntensity: 20,
            },
        };
    }

    private getLegacyHdrFallbackId(preferredId?: string): string {
        if (typeof preferredId === 'string' && preferredId !== '' && preferredId !== '0') {
            return preferredId;
        }

        const currentId = this.getHDRBackgroundId();
        if (currentId && currentId !== '0') {
            return currentId;
        }

        const firstFromPresetList = this.getPresetLists().hdr.find((item) => item.id && item.id !== '0');
        if (firstFromPresetList?.id) {
            return firstFromPresetList.id;
        }

        const firstAvailable = this.getHDRBackgroundList().find((item) => item.id && item.id !== '0');
        return firstAvailable?.id ?? '0';
    }

    private normalizeSettingsPatch(settings: EngineSettingsPatch): EngineSettingsPatch {
        if (!settings.environment) {
            return settings;
        }

        const environment = { ...settings.environment };
        if (environment.type === 'hdr') {
            const hdrId = this.getLegacyHdrFallbackId(environment.hdrId);
            if (hdrId !== '0') {
                environment.hdrId = hdrId;
            }
        }

        return {
            ...settings,
            environment,
        };
    }

    private async applyLegacyEnvironmentSettings(environment: Partial<EngineSettings['environment']>): Promise<void> {
        if (environment.type === 'none') {
            this.setHDRBackgroundId('0');
            this.callSettingMethod('setHDRBackgroundVisibility', false);
            return;
        }

        if (environment.type === 'hdr') {
            const hdrId = this.getLegacyHdrFallbackId(environment.hdrId);
            if (hdrId === '0') {
                console.warn('[Engine] Cannot enable HDR background: no HDR preset is available in compatibility mode.');
                return;
            }

            this.callSettingMethod('setHDRBackgroundVisibility', true);
            this.setHDRBackgroundId(hdrId);
            return;
        }

        if (environment.type === 'sky') {
            const skyPresetResult = typeof environment.skyPreset === 'string'
                ? this.callSettingMethod('setSkyPreset', environment.skyPreset)
                : undefined;
            if (skyPresetResult === undefined) {
                console.warn('[Engine] Sky environment switching is unavailable in compatibility mode.');
            }
        }
    }

    public async setSettings(settings: EngineSettingsPatch): Promise<void> {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set settings: engine not initialized.');
            return;
        }

        const normalizedSettings = this.normalizeSettingsPatch(settings);

        if (this.hasSettingMethod('setSettings')) {
            try {
                const setSettingsResult = this.callSettingMethod('setSettings', normalizedSettings);
                await Promise.resolve(setSettingsResult);
                return;
            } catch (error) {
                console.warn('[Engine] Native setSettings failed, fallback to compatibility mode.', error);
            }
        }

        if (normalizedSettings.render?.mode) {
            this.setRenderMode(normalizedSettings.render.mode);
        }
        if (typeof normalizedSettings.render?.lightIntensity === 'number') {
            this.setAmbientLightIntensity(normalizedSettings.render.lightIntensity);
        }
        if (typeof normalizedSettings.render?.contrast === 'number') {
            this.setSceneContrast(normalizedSettings.render.contrast);
        }
        if (typeof normalizedSettings.render?.saturation === 'number') {
            this.setSceneSaturation(normalizedSettings.render.saturation);
        }

        if (typeof normalizedSettings.display?.showEdge === 'boolean') {
            if (normalizedSettings.display.showEdge) {
                this.activeModelEdge();
            } else {
                this.disActiveModelEdge();
            }
        }
        if (typeof normalizedSettings.display?.edgeOpacity === 'number') {
            this.callSettingMethod('setEdgeOpacity', normalizedSettings.display.edgeOpacity);
        }
        if (typeof normalizedSettings.display?.showGrid === 'boolean') {
            this.callSettingMethod('setShowGrid', normalizedSettings.display.showGrid);
        }
        if (typeof normalizedSettings.display?.showLevel === 'boolean') {
            this.callSettingMethod('setShowLevel', normalizedSettings.display.showLevel);
        }
        if (typeof normalizedSettings.display?.groundId === 'string') {
            this.setGroundId(normalizedSettings.display.groundId);
        }
        if (typeof normalizedSettings.display?.groundHeight === 'number') {
            this.setGroundElevation(normalizedSettings.display.groundHeight);
        }

        const hasNativeSetEnvironmentType = this.hasSettingMethod('setEnvironmentType');
        const hasNativeSetHdrId = this.hasSettingMethod('setHdrId');

        if (typeof normalizedSettings.environment?.type === 'string') {
            if (hasNativeSetEnvironmentType) {
                const result = this.callSettingMethod('setEnvironmentType', normalizedSettings.environment.type);
                if (result !== undefined) {
                    await Promise.resolve(result);
                }
            } else {
                await this.applyLegacyEnvironmentSettings(normalizedSettings.environment);
            }
        }
        if (typeof normalizedSettings.environment?.hdrId === 'string') {
            if (hasNativeSetHdrId) {
                const result = this.callSettingMethod('setHdrId', normalizedSettings.environment.hdrId);
                if (result !== undefined) {
                    await Promise.resolve(result);
                }
            } else if (normalizedSettings.environment.type !== 'none') {
                this.setHDRBackgroundId(normalizedSettings.environment.hdrId);
            }
        }
        if (typeof normalizedSettings.environment?.hdrIntensity === 'number') {
            this.callSettingMethod('setHdrIntensity', normalizedSettings.environment.hdrIntensity);
        }
        if (typeof normalizedSettings.environment?.skyPreset === 'string') {
            this.callSettingMethod('setSkyPreset', normalizedSettings.environment.skyPreset);
        }
        if (typeof normalizedSettings.environment?.skyIntensity === 'number') {
            this.callSettingMethod('setSkyIntensity', normalizedSettings.environment.skyIntensity);
        }
        if (normalizedSettings.environment?.skyParams && typeof normalizedSettings.environment.skyParams === 'object') {
            this.callSettingMethod('setSkyParams', normalizedSettings.environment.skyParams);
        }

        if (typeof normalizedSettings.render?.shadowIntensity === 'number') {
            this.callSettingMethod('setShadowIntensity', normalizedSettings.render.shadowIntensity);
        }
        if (typeof normalizedSettings.render?.gtaoIntensity === 'number') {
            this.callSettingMethod('setGtaoIntensity', normalizedSettings.render.gtaoIntensity);
        }
    }

    public async resetToDefault(): Promise<void> {
        const settingApi = this.getSettingApi();
        const resetApi = settingApi?.resetToDefault;
        if (typeof resetApi === 'function') {
            await Promise.resolve(resetApi.call(this.engine.setting));
            return;
        }

        await this.setSettings({
            render: {
                mode: 'advanced',
                contrast: 50,
                saturation: 50,
                shadowIntensity: 50,
                lightIntensity: 50,
                gtaoIntensity: 50,
            },
            display: {
                showEdge: false,
                edgeOpacity: 30,
                showGrid: false,
                showLevel: false,
                showGround: false,
                groundId: '0',
                groundHeight: 0,
            },
            environment: {
                type: 'none',
                hdrId: '0',
                hdrIntensity: 20,
                skyPreset: 'sunrise_clear',
                skyParams: {},
                skyIntensity: 20,
            },
        });
    }

    // ---- 边线 ----

    /** 启用模型边线显示 */
    public activeModelEdge(): void {
        if (!this._isInitialized || !this.engine?.modelEdge) {
            console.warn('[Engine] Cannot active model edge: engine not initialized.');
            return;
        }
        this.engine.modelEdge.active();
    }

    /** 关闭模型边线显示 */
    public disActiveModelEdge(): void {
        if (!this._isInitialized || !this.engine?.modelEdge) {
            return;
        }
        this.engine.modelEdge.disActive();
    }

    /** 获取边线显示状态 */
    public getModelEdgeActive(): boolean {
        if (!this._isInitialized || !this.engine?.modelEdge) {
            return false;
        }
        return this.engine.modelEdge.getActive?.() ?? false;
    }

    // ---- 光照强度 ----

    /**
     * 设置环境光照强度
     * @param intensity 0-100
     */
    public setAmbientLightIntensity(intensity: number): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set ambient light intensity: engine not initialized.');
            return;
        }
        this.engine.setting.setAmbientLightIntensity(intensity);
    }

    /** 获取当前环境光照强度 */
    public getAmbientLightIntensity(): number {
        if (!this._isInitialized || !this.engine?.setting) {
            return 50;
        }
        return this.engine.setting.getAmbientLightIntensity?.() ?? 50;
    }

    // ---- 对比度 ----

    /**
     * 设置场景对比度
     * @param contrast 0-100
     */
    public setSceneContrast(contrast: number): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set scene contrast: engine not initialized.');
            return;
        }
        this.engine.setting.setSceneContrast(contrast);
    }

    /** 获取当前场景对比度 */
    public getSceneContrast(): number {
        if (!this._isInitialized || !this.engine?.setting) {
            return 50;
        }
        return this.engine.setting.getSceneContrast?.() ?? 50;
    }

    // ---- 饾和度 ----

    /**
     * 设置场景饾和度
     * @param saturation 0-100
     */
    public setSceneSaturation(saturation: number): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set scene saturation: engine not initialized.');
            return;
        }
        this.engine.setting.setSceneSaturation(saturation);
    }

    /** 获取当前场景饾和度 */
    public getSceneSaturation(): number {
        if (!this._isInitialized || !this.engine?.setting) {
            return 50;
        }
        return this.engine.setting.getSceneSaturation?.() ?? 50;
    }

    // ---- 环境背景 ----

    /** 获取 HDR 背景列表 */
    public getHDRBackgroundList(): { name: string; id: string }[] {
        if (!this._isInitialized || !this.engine?.setting) {
            return [];
        }
        return this.engine.setting.getHDRBackground?.() ?? [];
    }

    /**
     * 设置当前 HDR 背景
     * @param id 背景 ID
     */
    public setHDRBackgroundId(id: string): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set HDR background: engine not initialized.');
            return;
        }
        this.engine.setting.setHDRBackgroundId(id);
    }

    /** 获取当前 HDR 背景 ID */
    public getHDRBackgroundId(): string {
        if (!this._isInitialized || !this.engine?.setting) {
            return '';
        }
        return this.engine.setting.getHDRBackgroundId?.() ?? '';
    }

    /**
     * 设置 HDR 背景可见性
     * @param visible 是否可见
     */
    public setHDRBackgroundVisibility(visible: boolean): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set HDR background visibility: engine not initialized.');
            return;
        }
        this.engine.setting.setHDRBackgroundVisibility(visible);
    }

    /** 获取 HDR 背景可见性 */
    public getHDRBackgroundVisibility(): boolean {
        if (!this._isInitialized || !this.engine?.setting) {
            return true;
        }
        return this.engine.setting.getHDRBackgroundVisibility?.() ?? true;
    }

    // ---- 地面 ----

    /** 获取地面列表 */
    public getGroundList(): { name: string; id: string }[] {
        if (!this._isInitialized || !this.engine?.setting) {
            return [];
        }
        return this.engine.setting.getGroundList?.() ?? [];
    }

    /**
     * 设置当前地面类型
     * @param id 地面 ID
     */
    public setGroundId(id: string): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set ground: engine not initialized.');
            return;
        }
        this.engine.setting.setGroundId(id);
    }

    /** 获取当前地面 ID */
    public getGroundId(): string {
        if (!this._isInitialized || !this.engine?.setting) {
            return '';
        }
        return this.engine.setting.getGroundId?.() ?? '';
    }

    /**
     * 设置地面高度（单位：m）
     * @param elevation 地面高度值
     */
    public setGroundElevation(elevation: number): void {
        if (!this._isInitialized || !this.engine?.setting) {
            console.warn('[Engine] Cannot set ground elevation: engine not initialized.');
            return;
        }
        this.engine.setting.setGroundElevation(elevation);
    }

    /** 获取当前地面高度（单位：m） */
    public getGroundElevation(): number {
        if (!this._isInitialized || !this.engine?.setting) {
            return 0;
        }
        return this.engine.setting.getGroundElevation?.() ?? 0;
    }

    /** 切回正交视角 */
    public switchToOrthographicCamera() {
        if (!this._isInitialized) {
            return 0;
        }
        this.engine.cameraModule.switchToOrthographicCamera()

    }

    // ==================== 结束：设置功能 ====================
    // ==================== 漫游功能 ====================

    /** 漫游模式是否激活 */
    private isWalkModeActive: boolean = false;

    /**
     * 激活第一人称漫游模式
     * @remarks 切换到第一人称控制器，禁用轨道控制器
     */
    public activateFirstPersonMode(): void {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot activate first person mode: engine not initialized.');
            return;
        }

        if (!this.engine.controlModule) {
            console.error('[Engine] Control module not available.');
            return;
        }

        if (this.isWalkModeActive) {
            console.log('[Engine] First person mode already active, skipping.');
            return;
        }

        console.log('[Engine] Activating first person mode');
        this.engine.controlModule.switchFirstPersonMode();
        this.isWalkModeActive = true;
    }

    /**
     * 停用第一人称漫游模式
     * @remarks 切换回轨道控制器
     */
    public deactivateFirstPersonMode(): void {
        if (!this._isInitialized || !this.engine?.controlModule) {
            return;
        }

        if (!this.isWalkModeActive) {
            return;
        }

        this.engine.controlModule.switchDefaultMode();
        console.log('[Engine] Deactivating first person mode');
        this.isWalkModeActive = false;
    }

    /**
     * 获取漫游移动速度
     * @returns 当前移动速度值
     */
    public getWalkSpeed(): number {
        if (!this._isInitialized) {
            console.error('[Engine] Cannot get walk speed: engine not initialized.');
            return 1;
        }
        const speed = this.engine.controlModule.getMoveSpeed();
        console.log('[Engine] getWalkSpeed from controlModule:', speed);
        return speed;
    }

    /**
     * 设置漫游移动速度
     * @param speed 移动速度值
     */
    public setWalkSpeed(speed: number): void {
        if (!this._isInitialized || !this.engine?.controlModule) {
            console.error('[Engine] Cannot set walk speed: engine not initialized.');
            return;
        }
        this.engine.controlModule.setMoveSpeed(speed);
    }

    /**
     * 获取漫游重力开关状态
     * @returns 是否启用重力
     */
    public getWalkGravity(): boolean {
        if (!this._isInitialized) {
            console.error('[Engine] Cannot get walk gravity: engine not initialized.');
            return false;
        }
        const gravity = this.engine.controlModule.getApplyGravity();
        console.log('[Engine] getWalkGravity from controlModule:', gravity);
        return gravity;
    }

    /**
     * 设置漫游重力开关
     * @param enabled 是否启用重力
     */
    public setWalkGravity(enabled: boolean): void {
        if (!this._isInitialized) {
            console.error('[Engine] Cannot set walk gravity: engine not initialized.');
            return;
        }
        this.engine.controlModule.setApplyGravity(enabled);
    }

    /**
     * 获取漫游碰撞检测开关状态
     * @returns 是否启用碰撞检测
     */
    public getWalkCollision(): boolean {
        if (!this._isInitialized) {
            console.error('[Engine] Cannot get walk collision: engine not initialized.');
            return false;
        }
        const collision = this.engine.controlModule.getApplyCollision();
        console.log('[Engine] getWalkCollision from controlModule:', collision);
        return collision;
    }

    /**
     * 设置漫游碰撞检测开关
     * @param enabled 是否启用碰撞检测
     */
    public setWalkCollision(enabled: boolean): void {
        if (!this._isInitialized || !this.engine?.controlModule) {
            console.error('[Engine] Cannot set walk collision: engine not initialized.');
            return;
        }
        this.engine.controlModule.setApplyCollision(enabled);
    }

    /**
     * 切换小地图显示状态
     */
    public toggleMiniMap(): void {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot toggle mini map: engine not initialized.');
            return;
        }
        this.engine.minMap?.toggle();
    }

    /**
     * 获取小地图显示状态
     * @returns true=显示，false=隐藏
     */
    public getMiniMapState(): boolean {
        if (!this._isInitialized || !this.engine) {
            return false;
        }
        return this.engine.minMap?.getstate() ?? false;
    }

    /**
     * 获取漫游模式是否激活
     * @returns 是否处于漫游模式
     */
    public isFirstPersonModeActive(): boolean {
        return this.isWalkModeActive;
    }

    // ==================== 结束：漫游功能 ====================

    // ==================== 构件选中 ====================

    /**
     * 获取当前选中的构件
     * @returns 选中构件的 URL 和 ID，未选中时返回 null
     */
    public getSelectedComponent(): { url: string; id: string } | null {
        return this.selectedComponent;
    }

    /**
     * 获取构件属性
     * @param url 模型 URL
     * @param id 构件 ID
     * @param callback 回调函数，接收属性数据
     */
    public getComponentProperties(
        url: string,
        id: string,
        callback: (data: any) => void
    ): void {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot get component properties: engine not initialized.');
            return;
        }

        if (!this.engine.modelProperties) {
            console.error('[Engine] modelProperties not available');
            return;
        }

        console.log('[Engine] Getting component properties:', { url, id });
        this.engine.modelProperties.getModelProperties(url, id, callback);
    }

    /**
     * 获取构件位置和详细信息（问题报告用）
     * @param params 查询参数
     * @param params.url 模型 URL
     * @param params.ids 构件 ID 数组
     * @returns 构件数据，包含位置、属性等信息
     */
    public getModelPosition(params: { url: string; ids: number[] }): any {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot get model position: engine not initialized.');
            return null;
        }

        if (!this.engine.issueReport?.getMoldePosition) {
            console.error('[Engine] getMoldePosition method not available in issueReport');
            return null;
        }

        console.log('[Engine] Getting model position:', params);

        // 调用底层引擎 issueReport.getMoldePosition 获取构件信息
        return this.engine.issueReport.getMoldePosition(params);
    }

    /**
     * 跳转到指定相机视角（问题报告用）
     * @param cameraData 相机配置数据，包含 position、target、rotation 等信息
     * @returns 是否跳转成功
     */
    public jumpToCamera(cameraData: any): boolean {
        if (!this._isInitialized || !this.engine) {
            console.error('[Engine] Cannot jump to camera: engine not initialized.');
            return false;
        }

        if (!this.engine.issueReport?.jumptoCamera) {
            console.error('[Engine] jumptoCamera method not available in issueReport');
            return false;
        }

        console.log('[Engine] Jumping to camera:', cameraData);

        // 调用底层引擎 issueReport.jumptoCamera 跳转视角
        try {
            this.engine.issueReport.jumptoCamera(cameraData);
            return true;
        } catch (error) {
            console.error('[Engine] Failed to jump to camera:', error);
            return false;
        }
    }

    // ==================== 结束：构件选中 ====================

    // ==================== 模型树 ====================

    /**
     * 获取类型树数据
     * @returns 类型树节点数组
     */
    public getTypeTreeData(): any[] {
        if (!this._isInitialized || !this.engine?.modelTree) {
            return [];
        }
        return this.engine.modelTree.getTypeTreeData();
    }

    /**
     * 获取楼层树数据
     * @returns 楼层树节点数组
     */
    public getLevelTreeData(): any[] {
        if (!this._isInitialized || !this.engine?.modelTree) {
            return [];
        }
        return this.engine.modelTree.getLevelTreeData();
    }

    /**
     * 获取专业树数据
     * @returns 专业树节点数组
     */
    public getMajorTreeData(): any[] {
        if (!this._isInitialized || !this.engine?.modelTree) {
            return [];
        }
        return this.engine.modelTree.getMajorTreeData();
    }

    /**
     * 获取所有已加载模型的视图树数据
     * @returns 模型视图树数组
     */
    public getAllViewTreeData(): ModelViewTree[] {
        if (!this._isInitialized || !this.engine?.viewTree) {
            return [];
        }
        return this.engine.viewTree.getAllViewTreeData();
    }

    /**
     * 打开指定模型的指定视图
     * @param url 模型文件地址
     * @param viewId 视图 ID
     */
    public openView(url: string, viewId: string): void {
        if (!this._isInitialized || !this.engine?.viewTree) {
            console.warn('[Engine] Cannot open view: engine not initialized or viewTree not available.');
            return;
        }
        try {
            this.engine.viewTree.openView(url, viewId);
        } catch (e) {
            console.warn('[Engine] openView failed:', e);
        }
    }

    // ==================== 结束：模型树 ====================

    /**
     * 激活框选放大功能
     * @remarks 允许用户通过框选区域来放大视图
     */
    public activateZoomBox(): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Engine not initialized.');
            return;
        }
        this.engine.rangeScale?.active();
    }

    /**
     * 获取引擎基本信息
     * @returns 引擎信息对象，包含构件数量、三角面数量、顶点数量等
     */
    public getEngineInfo(): EngineInfo | null {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Engine not initialized.');
            return null;
        }
        if (!this.engine.engineInfo || typeof this.engine.engineInfo.getEngineInfo !== 'function') {
            console.warn('[Engine] engineInfo module not available.');
            return null;
        }
        const info = this.engine.engineInfo.getEngineInfo();
        console.log('[Engine] getEngineInfo raw:', info);
        return info as EngineInfo;
    }

    // ==================== 路径漫游 ====================

    /**
     * 添加漫游点
     * 将当前相机位置添加为一个漫游点
     */
    public pathRoamingAddPoint(): void {
        // 检查引擎是否已初始化
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        // 调用底层 API 添加点位
        this.engine.pathRoaming.addPoint(0);
    }

    /**
     * 删除指定索引的漫游点
     * @param index 要删除的漫游点索引
     */
    public pathRoamingRemovePoint(index: number): void {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        this.engine.pathRoaming.removePoint(index);
    }

    /**
     * 清除所有漫游点
     */
    public pathRoamingClearPoints(): void {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        this.engine.pathRoaming.clearPoints();
    }

    /**
     * 获取所有漫游点
     * @returns 漫游点数组
     */
    public pathRoamingGetPoints(): any[] {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return [];
        }
        return this.engine.pathRoaming.getPoints() ?? [];
    }

    /**
     * 跳转到指定漫游点
     * @param index 目标漫游点索引
     */
    public pathRoamingJumpToPoint(index: number): void {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        this.engine.pathRoaming.jumpToPoint(index);
    }

    /**
     * 播放漫游
     * @param options 播放选项，包含时长、循环、回调等配置
     */
    public pathRoamingPlay(options?: {
        duration?: number;
        loop?: boolean;
        onComplete?: () => void;
        onPointComplete?: (pointIndex: number) => void;
    }): void {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        this.engine.pathRoaming.play(options);
    }

    /**
     * 停止漫游
     */
    public pathRoamingStop(): void {
        if (!this._isInitialized || !this.engine?.pathRoaming) {
            console.warn('[Engine] pathRoaming not available');
            return;
        }
        this.engine.pathRoaming.stop();
    }

    // ==================== 结束：路径漫游 ====================

    // ==================== 构件操作 ====================

    /**
     * 获取当前高亮（选中）的模型
     * @returns 高亮模型对象，未选中时返回 null
     */
    public getHighlightModels(): any {
        if (!this._isInitialized || !this.engine) {
            return null;
        }
        return this.engine.engineStatus?.highlightModels ?? null;
    }

    /**
     * 高亮指定模型构件
     * 
     * 用于在 3D 场景中高亮显示指定的构件，常用于：
     * - 点击构件树节点时高亮对应模型
     * - 搜索结果定位
     * - 批量选中构件
     * 
     * @param models - 要高亮的模型数组，每个元素包含:
     *   - url: 模型资源 URL
     *   - ids: 构件 ID 数组
     * 
     * @example
     * engine.highlightModel([
     *   { url: 'https://xxx/models/xxx/', ids: [350518, 350520] }
     * ]);
     */
    public highlightModel(models: { url: string; ids: number[] }[]): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot highlight model: engine not initialized.');
            return;
        }
        const safeModels = this.sanitizeModelParams(models);
        if (!safeModels.length) return;
        try {
            this.engine.modelToolModule.highlightModel(safeModels);
        } catch (e) {
            console.warn('[Engine] highlightModel failed:', e);
        }
    }

    public unhighlightAllModels(): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot unhighlight models: engine not initialized.');
            return;
        }
        this.engine.modelToolModule.unhighlightAllModels();
    }

    public viewScaleToModel(models: { url: string; ids: number[] }[]): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot view scale to model: engine not initialized.');
            return;
        }
        const safeModels = this.sanitizeModelParams(models);
        if (!safeModels.length) return;
        try {
            this.engine.modelToolModule.viewScaleToModel(safeModels);
        } catch (e) {
            console.warn('[Engine] viewScaleToModel failed:', e);
        }
    }

    public hideModels(models: { url: string; ids: number[] }[]): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot hide models: engine not initialized.');
            return;
        }
        const safeModels = this.sanitizeModelParams(models);
        if (!safeModels.length) return;
        try {
            this.engine.modelToolModule.hideModel(safeModels);
        } catch (e) {
            console.warn('[Engine] hideModels failed:', e);
        }
    }

    public showModel(models: { url: string; ids: number[] }[]): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot show model: engine not initialized.');
            return;
        }
        const safeModels = this.sanitizeModelParams(models);
        if (!safeModels.length) return;
        try {
            this.engine.modelToolModule.showModel(safeModels);
        } catch (e) {
            console.warn('[Engine] showModel failed:', e);
        }
    }

    private sanitizeModelParams(models: { url: string; ids: number[] }[]): { url: string; ids: number[] }[] {
        if (!Array.isArray(models)) return [];

        const normalizeUrl = (url: string): string => url.replace(/\/+$/, '');

        const loadedModels = Array.isArray((this.engine as any)?.models)
            ? ((this.engine as any).models as Array<{ url?: string; nodesMap?: Map<number, { indexes?: unknown[] }> }>)
            : [];
        const modelMap = new Map<string, { nodesMap?: Map<number, { indexes?: unknown[] }> }>();
        for (const loadedModel of loadedModels) {
            if (loadedModel?.url) {
                modelMap.set(normalizeUrl(loadedModel.url), loadedModel);
            }
        }

        const result: { url: string; ids: number[] }[] = [];
        for (const model of models) {
            if (!model || typeof model.url !== 'string' || !model.url) continue;
            if (!Array.isArray(model.ids)) continue;

            const loadedModel = modelMap.get(normalizeUrl(model.url));
            if (!loadedModel || !(loadedModel.nodesMap instanceof Map)) {
                continue;
            }
            const idsSet = new Set<number>();
            for (const rawId of model.ids) {
                if (!Number.isFinite(rawId)) continue;
                const id = Number(rawId);
                if (!Number.isInteger(id)) continue;

                const node = loadedModel.nodesMap.get(id);
                if (!node || !Array.isArray(node.indexes) || node.indexes.length === 0) {
                    continue;
                }

                idsSet.add(id);
            }

            const ids = Array.from(idsSet);
            if (!ids.length) continue;

            result.push({ url: model.url, ids });
        }

        return result;
    }

    /**
     * 半透明指定模型
     * @param models 要半透明的模型对象
     */
    public translucentModels(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot translucent models: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.translucentModel(models);
        }
    }

    /**
     * 取消半透明
     */
    public unTranslucentModel(): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot cancel translucent: engine not initialized.');
            return;
        }
        this.engine.modelToolModule.unTranslucentModel();
    }

    /**
     * 隔离指定模型（隐藏其他）
     * @param models 要隔离的模型对象
     */
    public isolateModels(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot isolate models: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.isolateModel(models);
        }
    }

    /**
     * 半透明其他构件
     * @param models 选中的模型对象（其他构件将被半透明）
     */
    public translucentOtherModels(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot translucent other models: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.translucentOtherModel(models);
        }
    }

    /**
     * 显示所有模型
     */
    public showAllModels(): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot show all models: engine not initialized.');
            return;
        }
        // 取消剖切
        this.deactivateSection()
        // 显示所有模型
        this.engine.modelToolModule.showAllModels();
    }

    /**
     * 批量选择同类模型
     * @param models 参考模型对象
     */
    public batchSelectSameTypeModel(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot batch select: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.batchSelectSameTypeModel(models);
        }
    }

    /**
     * 批量选择同层模型
     * @param models 参考模型对象
     */
    public batchSelectSameLevelModel(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot batch select: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.batchSelectSameLevelModel(models);
        }
    }

    /**
     * 批量选择同层同类模型
     * @param models 参考模型对象
     */
    public batchSelectSameLevelTypeModel(models: any): void {
        if (!this._isInitialized || !this.engine?.modelToolModule) {
            console.warn('[Engine] Cannot batch select: engine not initialized.');
            return;
        }
        if (models) {
            this.engine.modelToolModule.batchSelectSameLevelTypeModel(models);
        }
    }

    // ==================== 结束：构件操作 ====================

    /**
     * 读取所有已加载模型的缓存编码数据
     * @remarks 遍历 engine.models，对每个模型调用 oneClickEncoding.readModelCodeFormStoge(url)
     */
    public readModelCodeFormStoge(): void {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Cannot read model code form stoge: engine not initialized.');
            return;
        }
        let count = 0;
        this.engine.models.forEach((m: any) => {
            this.engine.oneClickEncoding.readModelCodeFormStoge(m.url);
            count++;
        });
        console.log('readModelCodeFormStoge-success:', count);
    }

    /**
     * 检查所有已加载模型是否已有编码数据
     * @returns 当所有模型都存在编码时返回 true，否则返回 false
     */
    public hasModelCode(): boolean {
        if (!this._isInitialized || !this.engine) {
            console.warn('[Engine] Cannot check model code: engine not initialized.');
            return false;
        }
        const models = (this.engine as any).models;
        if (!Array.isArray(models) || models.length === 0) {
            return false;
        }
        const oneClickEncoding = (this.engine as any).oneClickEncoding;
        if (!oneClickEncoding || typeof oneClickEncoding.exitModelCode !== 'function') {
            console.warn('[Engine] oneClickEncoding.exitModelCode is not available.');
            return false;
        }
        return models.every((m: any) => {
            if (!m || typeof m.url !== 'string') {
                return false;
            }
            return oneClickEncoding.exitModelCode(m.url) === true;
        });
    }

    /**
     * 销毁组件 (接口实现)
     * 清理资源、取消订阅、销毁引擎实例
     */
    public destroy(): void {
        if (this._isDestroyed) {
            return;
        }

        // 停用测量功能
        this.deactivateMeasure();

        // 取消主题订阅
        if (this.unsubscribeTheme) {
            this.unsubscribeTheme();
            this.unsubscribeTheme = null;
        }

        // 销毁 3D 引擎，释放 GPU 资源
        if (this.engine) {
            this.engine.dispose();
            this.engine = null;
        }

        // 清理容器
        this.container.innerHTML = '';

        // 更新状态
        this.currentMeasureType = null;
        this.isMeasureActive = false;
        this._isDestroyed = true;
        this._isInitialized = false;
    }
}
